import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { TechnicalCooperationRow } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SidebarProductScroller from "../home/components/SidebarProductScroller";
import SidebarCompanyScroller from "../home/components/SidebarCompanyScroller";

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/tech-cooperation-api`;

const autoLinkContent = (html: string): string => {
  const parts = html.split(/(<[^>]+>)/g);
  return parts
    .map((part) => {
      if (part.startsWith("<") && part.endsWith(">")) return part;
      return part.replace(
        /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi,
        (url) => {
          const href = url.startsWith("http") ? url : `https://${url}`;
          return `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">${url}</a>`;
        }
      );
    })
    .join("");
};

const prepareContent = (content: string): string => {
  if (!content) return "";
  let html = content;
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
  if (!hasHtmlTags) {
    html = content
      .split(/\n\n+/)
      .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
      .join("");
  }
  html = autoLinkContent(html);
  html = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "b", "strong", "i", "em", "u", "s", "strike",
      "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li",
      "blockquote", "pre", "code",
      "a", "img",
      "span", "div",
      "table", "thead", "tbody", "tr", "th", "td",
      "hr",
    ],
    ALLOWED_ATTR: [
      "href", "src", "alt", "title", "target", "rel",
      "style", "class",
      "width", "height",
      "colspan", "rowspan",
    ],
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
    ADD_ATTR: ["target"],
  });
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("a").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer nofollow");
  });
  doc.querySelectorAll("img").forEach((img) => {
    img.setAttribute("onerror", "this.style.display='none'");
    img.style.maxWidth = "100%";
    img.style.borderRadius = "8px";
    img.style.height = "auto";
  });
  return doc.body.innerHTML;
};

const TechCooperationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const navigate = useNavigate();

  const [article, setArticle] = useState<TechnicalCooperationRow | null>(null);
  const [allArticles, setAllArticles] = useState<TechnicalCooperationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetchArticle = async () => {
      setLoading(true);
      try {
        // Fetch main article
        const articleRes = await fetch(`${EDGE_URL}?id=${encodeURIComponent(id)}`, { method: "GET" });
        const articleJson = await articleRes.json();
        if (!articleRes.ok || !articleJson.data) {
          navigate("/hop-tac-ky-thuat", { replace: true });
          return;
        }
        const main: TechnicalCooperationRow = {
          id: articleJson.data.id as string,
          title: articleJson.data.title as string,
          category: (articleJson.data.category as string) || undefined,
          content: (articleJson.data.content as string) || undefined,
          image_url: (articleJson.data.image_url as string) || undefined,
          created_at: (articleJson.data.created_at as string) || undefined,
          updated_at: (articleJson.data.updated_at as string) || undefined,
        };
        setArticle(main);

        // Fetch other articles
        const listRes = await fetch(`${EDGE_URL}?limit=30&sort=newest`, { method: "GET" });
        const listJson = await listRes.json();
        if (listRes.ok && listJson.data) {
          const others = (listJson.data as Record<string, unknown>[])
            .filter((r) => r.id !== id)
            .map((r) => ({
              id: r.id as string,
              title: r.title as string,
              category: (r.category as string) || undefined,
              content: (r.content as string) || undefined,
              image_url: (r.image_url as string) || undefined,
              created_at: (r.created_at as string) || undefined,
              updated_at: (r.updated_at as string) || undefined,
            })) as TechnicalCooperationRow[];
          setAllArticles(others);
        }
      } catch (err) {
        console.error("Detail fetch error:", err);
        navigate("/hop-tac-ky-thuat", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id, navigate]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <div className="max-w-[800px] mx-auto px-6 py-16 animate-pulse space-y-6">
          <div className="h-4 bg-gray-200 rounded w-1/4" />
          <div className="h-8 bg-gray-200 rounded" />
          <div className="h-64 bg-gray-200 rounded-2xl" />
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${85 + Math.random() * 15}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      <div className="flex items-stretch relative">
        {/* Left Sidebar */}
        <div className="hidden xl:flex flex-shrink-0 w-[180px]">
          <div
            className="sticky top-[72px] self-start w-[180px]"
            style={{ height: "calc(100vh - 72px)" }}
          >
            <SidebarProductScroller isKr={isKr} />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="max-w-[1100px] mx-auto px-4 py-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-8">
              <Link to="/" className="hover:text-[#004A99] cursor-pointer">
                {isKr ? "\ud648" : "Trang ch\u1ee7"}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to="/hop-tac-ky-thuat"
                className="hover:text-[#004A99] cursor-pointer"
              >
                {isKr ? "\uae30\uc220 \ud611\ub825" : "H\u1ee3p T\u00e1c K\u1ef9 Thu\u1eadt"}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-gray-500 line-clamp-1">{article.title}</span>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Article */}
              <article className="flex-1 min-w-0">
                {article.category && (
                  <div className="mb-4">
                    <span className="text-xs font-bold text-[#004A99] bg-[#004A99]/10 px-3 py-1 rounded-full">
                      {article.category}
                    </span>
                  </div>
                )}

                <h1
                  className="text-2xl lg:text-3xl font-black text-gray-800 leading-tight mb-4"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  {article.title}
                </h1>

                <div className="flex items-center gap-3 text-sm text-gray-400 mb-6">
                  <i className="ri-calendar-line" />
                  <span>{formatDate(article.created_at)}</span>
                </div>

                {article.image_url && (
                  <div
                    className="w-full h-80 lg:h-96 rounded-2xl overflow-hidden mb-8 cursor-pointer"
                    onClick={() => setLightboxImg(article.image_url!)}
                  >
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}

                {article.content && (
                  <div
                    className="article-content mb-8"
                    dangerouslySetInnerHTML={{
                      __html: prepareContent(article.content),
                    }}
                  />
                )}

                <style>{`
                  .article-content {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    font-size: 15px;
                    line-height: 1.8;
                    color: #374151;
                    white-space: pre-wrap;
                  }
                  .article-content p { margin: 0.75em 0; min-height: 1.2em; }
                  .article-content h1 { font-size: 1.75em; font-weight: 700; margin: 1em 0 0.5em; color: #111827; line-height: 1.3; }
                  .article-content h2 { font-size: 1.4em; font-weight: 700; margin: 0.9em 0 0.45em; color: #111827; line-height: 1.35; }
                  .article-content h3 { font-size: 1.2em; font-weight: 600; margin: 0.8em 0 0.4em; color: #1f2937; line-height: 1.4; }
                  .article-content b, .article-content strong { font-weight: 700; color: #111827; }
                  .article-content i, .article-content em { font-style: italic; }
                  .article-content u { text-decoration: underline; }
                  .article-content s, .article-content strike { text-decoration: line-through; }
                  .article-content ul { list-style-type: disc; padding-left: 1.5em; margin: 0.75em 0; }
                  .article-content ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.75em 0; }
                  .article-content li { margin: 0.35em 0; }
                  .article-content blockquote {
                    border-left: 4px solid #004A99;
                    padding: 10px 16px;
                    color: #4b5563;
                    font-style: italic;
                    margin: 1em 0;
                    background: #f8f9fa;
                    border-radius: 0 8px 8px 0;
                  }
                  .article-content a {
                    color: #0066cc;
                    text-decoration: underline;
                    cursor: pointer;
                    transition: color 0.15s ease;
                    word-break: break-all;
                  }
                  .article-content a:hover { color: #004A99; }
                  .article-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
                  .article-content pre, .article-content code {
                    background: #f3f4f6;
                    border-radius: 6px;
                    padding: 2px 6px;
                    font-family: 'Courier New', monospace;
                    font-size: 0.9em;
                  }
                  .article-content pre { padding: 12px 16px; overflow-x: auto; margin: 0.75em 0; }
                  .article-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.5em 0; }
                  .article-content table { width: 100%; border-collapse: collapse; margin: 1em 0; font-size: 0.9em; }
                  .article-content th, .article-content td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; }
                  .article-content th { background: #f9fafb; font-weight: 600; }
                `}</style>

                <Link
                  to="/hop-tac-ky-thuat"
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#004A99] transition-colors cursor-pointer group"
                >
                  <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" />
                  {isKr
                    ? "\ubaa9\ub85d\uc73c\ub85c \ub3cc\uc544\uac00\uae30"
                    : "Quay l\u1ea1i danh s\u00e1ch"}
                </Link>
              </article>

              {/* Right sidebar */}
              <aside className="w-full lg:w-64 flex-shrink-0 space-y-4">
                <div className="bg-white rounded-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-[#004A99] to-[#002d5f] px-4 py-3 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-white text-base" />
                    <p className="text-white font-bold text-sm">
                      {isKr ? "\uc804\uccb4 \uae30\uc0ac" : "T\u1ea5t c\u1ea3 b\u00e0i vi\u1ebft"}
                    </p>
                    {allArticles.length > 0 && (
                      <span className="ml-auto text-white/60 text-xs">
                        {allArticles.length} {isKr ? "\uac1c" : "b\u00e0i"}
                      </span>
                    )}
                  </div>

                  <div className="divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
                    {allArticles.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-400 text-xs">
                        <i className="ri-file-list-3-line text-2xl block mb-2 text-gray-200" />
                        {isKr
                          ? "\ub2e4\ub978 \uae30\uc0ac\uac00 \uc5c6\uc2b5\ub2c8\ub2e4"
                          : "Ch\u01b0a c\u00f3 b\u00e0i vi\u1ebft kh\u00e1c"}
                      </div>
                    ) : (
                      allArticles.map((art) => (
                        <Link
                          key={art.id}
                          to={`/hop-tac-ky-thuat/${art.id}`}
                          className="flex gap-3 px-4 py-3 hover:bg-[#004A99]/5 transition-colors group cursor-pointer"
                        >
                          <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                            {art.image_url ? (
                              <img
                                src={art.image_url}
                                alt={art.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#004A99]/10 to-[#00AEEF]/10">
                                <i className="ri-file-list-3-line text-[#004A99]/30 text-lg" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {art.category && (
                              <span className="text-[9px] font-bold text-[#004A99] bg-[#004A99]/10 px-1.5 py-0.5 rounded-full inline-block mb-1">
                                {art.category}
                              </span>
                            )}
                            <p className="text-xs font-semibold text-gray-700 group-hover:text-[#004A99] line-clamp-2 leading-snug transition-colors">
                              {art.title}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-1">
                              {art.created_at ? formatDate(art.created_at) : ""}
                            </p>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>

                  <div className="border-t border-gray-100 px-4 py-3">
                    <Link
                      to="/hop-tac-ky-thuat"
                      className="flex items-center justify-center gap-2 text-xs text-[#004A99] font-semibold hover:underline cursor-pointer"
                    >
                      <i className="ri-list-check-2" />
                      {isKr
                        ? "\uc804\uccb4 \ubaa9\ub85d \ubcf4\uae30"
                        : "Xem to\u00e0n b\u1ed9 danh s\u00e1ch"}
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="hidden xl:flex flex-shrink-0 w-[180px]">
          <div
            className="sticky top-[72px] self-start w-[180px]"
            style={{ height: "calc(100vh - 72px)" }}
          >
            <SidebarCompanyScroller isKr={isKr} />
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <img
            src={lightboxImg}
            alt="Preview"
            className="max-w-full max-h-[90vh] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxImg(null)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TechCooperationDetailPage;